import React from 'react'
import { observer } from 'mobx-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import styled from 'styled-components'

import Spacer from '~/components/Spacer'
import useInject from '~/hooks/useInject'
import { DISCLAIMER_TYPES } from '~/utilities/constants'

const DisclaimerText = styled.div`
  font-family: ${(props) => props.theme.fonts.avenirNextMedium};
  color: ${(props) => props.theme.colors.text.subContent};
  font-size: 0.95rem;
  text-align: justify;
`

const StyledLink = styled.a`
  display: inline-block;
  color: rgba(48, 137, 243, 1);
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`

const WinItLawDisclaimerText = ({ translate }) => (
  <>
    {translate('traffic.searchAgreement.text1')}
    <b>"{translate('form.general.submit.searchTickets')}"</b>
    {translate('traffic.searchAgreement.text2')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>
    {translate('traffic.searchAgreement.text3')}
    {translate('traffic.searchAgreement.text4')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>
    {translate('traffic.searchAgreement.text5')}
    <StyledLink
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/terms-of-service'
          : 'https://winitlaw.com/terms-of-service'
      }
    >
      Terms of Service
    </StyledLink>
    {translate('traffic.searchAgreement.text6')}
    <StyledLink
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/privacy-policy'
          : 'https://winitlaw.com/privacy-policy'
      }
    >
      Privacy Policy
    </StyledLink>
    {translate('traffic.searchAgreement.text7')}
  </>
)

const WinitLlcDisclaimerText = ({ translate }) => (
  <>
    {translate('traffic.searchAgreement.text1')}
    <b>"{translate('form.general.submit.searchTickets')}"</b>
    {translate('traffic.searchAgreement.text2')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>
    {translate('traffic.searchAgreement.text3')}
    {translate('traffic.searchAgreement.text4')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>
    {translate('traffic.searchAgreement.text5')}
    <StyledLink
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/terms-of-service'
          : 'https://winitlaw.com/terms-of-service'
      }
    >
      Terms of Service
    </StyledLink>
    {translate('traffic.searchAgreement.text6')}
    <StyledLink
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/privacy-policy'
          : 'https://winitlaw.com/privacy-policy'
      }
    >
      Privacy Policy
    </StyledLink>
    {translate('traffic.searchAgreement.text7')}
  </>
)

const DivorceDisclaimerText = ({ translate }) => (
  <>
    {translate('divorce.searchAgreement.text1')}
    <b>“{translate('form.general.submit.searchRecords')}”</b>
    {translate('divorce.searchAgreement.text2')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>
    {translate('divorce.searchAgreement.text3')}
    {translate('divorce.searchAgreement.text4')}
    {translate('divorce.searchAgreement.text5')}
    <StyledLink href="mailto:support@winitlaw.com">support@winitlaw.com</StyledLink>.
    {translate('divorce.searchAgreement.text6')}
    <Link
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/terms-of-service'
          : 'https://winitlaw.com/terms-of-service'
      }
    >
      {translate('general.termsOfService')}
    </Link>
    {translate('divorce.searchAgreement.text7')}
    <Link
      href={
        process.env.WINIT_ENV === 'development' || process.env.WINIT_ENV === 'staging'
          ? 'https://staging.winitlaw.com/privacy-policy'
          : 'https://winitlaw.com/privacy-policy'
      }
    >
      {translate('general.privacyPolicy')}
    </Link>
    {translate('divorce.searchAgreement.text8')}
  </>
)

const DISCLAIMER_COMPONENTS = {
  [DISCLAIMER_TYPES.winitLaw]: WinItLawDisclaimerText,
  [DISCLAIMER_TYPES.divorce]: DivorceDisclaimerText,
  [DISCLAIMER_TYPES.winitLlc]: WinitLlcDisclaimerText,
}

const mapStore = (store) => ({
  isMobile: store.ui.isMobile,
})

const LookupDisclaimer = ({ type = DISCLAIMER_TYPES.winitLlc }) => {
  const translate = useTranslations()
  const { isMobile } = useInject(mapStore)

  return (
    <>
      <DisclaimerText isMobile={isMobile}>
        {DISCLAIMER_COMPONENTS[type]({ translate })}
      </DisclaimerText>

      <Spacer mobile={20} desktop={20} />

      <DisclaimerText isMobile={isMobile}>
        <b>*</b>
        {[DISCLAIMER_TYPES.winitLaw, DISCLAIMER_TYPES.divorce].includes(type)
          ? translate('winItLawDisclaimer')
          : translate('disclaimer')}
      </DisclaimerText>

      {[DISCLAIMER_TYPES.winitLaw, DISCLAIMER_TYPES.divorce].includes(type) && (
        <>
          <Spacer mobile={20} desktop={20} />
          <DisclaimerText isMobile={isMobile}>
            <b>**</b>
            {translate('bwLawDisclaimer')}
          </DisclaimerText>
        </>
      )}
    </>
  )
}

export default observer(LookupDisclaimer)
